import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Grid,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { BasicCell, InvisibleCell } from '@/components/atoms/CompReportCells';
import { Loading } from '@/components/atoms/Loading';
import { MainCard } from '@/components/molecules/MainCard';
import { PeriodHeader } from '@/components/molecules/PeriodHeader';
import { useShiftsQuery } from '@/hooks/useShiftsQuery';
import { ReportsService } from '@/openapi';
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/util/queryKeys';
import { SchedulePeriod } from '@/util/enum';
import { nbsp } from '@/util/unicode';
import { ConfigParnterShifts } from './ConfigureShifts';
import { useModal } from 'react-modal-hook';
import { useAuth } from '@/providers/AuthProvider';

type ShiftDiscovered = {
  id: number;
  symbol: string;
};

const Quarters = [1, 2, 3, 4];
const colorQuarter = (theme: Theme, quarter: number) => {
  const colorMap = [
    theme.palette.primary,
    theme.palette.primary,
    theme.palette.secondary,
    theme.palette.orange,
    theme.palette.yellow,
  ];
  return lighten(colorMap[quarter].light, 0.6);
};

export const PartnerShiftsReport: React.FC = () => {
  const theme = useTheme();

  const [reportDate, setReportDate] = useState<Date>(new Date());
  const reportYear = reportDate.getFullYear();

  const { data: partnerShiftsReport, isLoading } = useQuery({
    queryKey: queryKeys.reports.shifts(reportYear),
    queryFn: () =>
      ReportsService.reportsServiceGetShiftTotalsReport({
        path: { year: reportYear },
      }),
  });
  const { data: shiftData } = useShiftsQuery();
  const { data: shiftsConfig } = useQuery({
    queryKey: queryKeys.shifts.config(),
    queryFn: () => ReportsService.reportsServiceGetShiftTotalsConfig(),
  });

  const shiftsInUse = useMemo(() => {
    if (!partnerShiftsReport || !shiftData) {
      return [];
    }
    const shiftsDiscovered = new Array<ShiftDiscovered>();
    partnerShiftsReport.data.partners.forEach((partner) => {
      partner.shifts.forEach((shift) => {
        if (!shiftsDiscovered.find((entry) => entry.id === shift.shiftId)) {
          shiftsDiscovered.push({ id: shift.shiftId, symbol: shift.shift });
        }
      });
    });
    const shiftsInUse = shiftsDiscovered
      .filter((a) => {
        const shiftA = shiftData.find((s) => s.id === a.id);
        if (!shiftA) {
          return false;
        }
        return shiftsConfig?.data.displayedShifts.includes(a.id);
      })
      .sort((a, b) => {
        const shiftA = shiftData.find((s) => s.id === a.id);
        const shiftB = shiftData.find((s) => s.id === b.id);
        if (!shiftA || !shiftB) {
          return 0;
        }
        return shiftA.rank - shiftB.rank;
      });
    return shiftsInUse;
  }, [partnerShiftsReport, shiftData, shiftsConfig]);

  const { isAdmin } = useAuth();

  const widthWithDays = Math.max(4 * 4 + 16 * shiftsInUse.length, 40);
  const widthSpec = theme.spacing(widthWithDays);

  const [showConfigShiftsModal, hideConfigShiftsModal] = useModal(() => (
    <ConfigParnterShifts hideModal={hideConfigShiftsModal} />
  ));

  return (
    <Grid size={12}>
      <Typography variant="h1">Partner Shifts Report</Typography>
      <Box
        sx={{
          m: 4,
        }}
      />
      {isAdmin && (
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
          }}>
          <Grid
            size={{
              xs: 8,
              md: 1,
            }}>
            <Button
              variant="contained"
              size="small"
              onClick={showConfigShiftsModal}>
              Configure
            </Button>
          </Grid>
        </Grid>
      )}
      <Box
        sx={{
          m: 2,
        }}
      />
      <MainCard sx={{ minWidth: widthSpec }}>
        <Grid
          container
          sx={{
            justifyContent: 'center',
            width: '100%',
          }}>
          <Grid>
            <Box
              sx={{
                minWidth: 100,
              }}
            />
          </Grid>
          <Grid
            sx={{
              alignSelf: 'center',
              marginRight: 'auto',
              marginLeft: 'auto',
            }}>
            <PeriodHeader
              period={SchedulePeriod.Annually}
              startDate={reportDate}
              onSelectDate={setReportDate}
            />
          </Grid>
          {/* <Grid item width={100} display="flex" justifyContent="right">
            <Button onClick={handlePrint}>
              <LocalPrintshop />
            </Button>
          </Grid> */}
        </Grid>
        {partnerShiftsReport && shiftsInUse.length > 0 ? (
          <Grid>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'top',
              }}>
              <InvisibleCell width={2} />
              <InvisibleCell width={0.5} />
              {shiftsInUse.map((shift) => (
                <Fragment key={shift.id}>
                  <BasicCell key={shift.id} width={5}>
                    {shift.symbol}
                  </BasicCell>
                  <InvisibleCell width={0.5} />
                </Fragment>
              ))}
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'top',
              }}>
              <InvisibleCell width={2} />
              <InvisibleCell width={0.5} />
              {shiftsInUse.map((shift) => (
                <Fragment key={shift.id}>
                  {Quarters.map((quarter) => (
                    <BasicCell key={quarter}>Q{quarter}</BasicCell>
                  ))}
                  <BasicCell key={'T'}>T</BasicCell>
                  <InvisibleCell width={0.5} />
                </Fragment>
              ))}
            </Grid>
            {partnerShiftsReport.data.partners
              .sort(
                (a, b) =>
                  a.partnerNickname?.localeCompare(b?.partnerNickname ?? '') ??
                  0
              )
              .map((partner) => (
                <Grid
                  key={partner.partnerId}
                  data-testid="partner-shifts-row"
                  sx={{
                    display: 'flex',
                    alignItems: 'top',
                  }}>
                  <BasicCell width={2} border={false}>
                    {partner.partnerNickname}
                  </BasicCell>
                  <InvisibleCell width={0.5} />
                  {shiftsInUse.map((shift) => {
                    const partnerShift = partner.shifts.find(
                      (s) => s.shiftId === shift.id
                    );
                    return (
                      <Fragment key={shift.id}>
                        {Quarters.map((quarter) => {
                          const partnerQuarter = partnerShift?.quarters.find(
                            (q) => q.quarter === quarter
                          );
                          return (
                            <BasicCell
                              key={quarter}
                              background={colorQuarter(theme, quarter)}>
                              {partnerQuarter?.count ?? nbsp}
                            </BasicCell>
                          );
                        })}
                        <BasicCell key={'T'}>
                          {partnerShift?.quarters.reduce(
                            (acc, curr) => acc + curr.count,
                            0
                          ) ?? nbsp}
                        </BasicCell>
                        <InvisibleCell width={0.5} />
                      </Fragment>
                    );
                  })}
                </Grid>
              ))}
          </Grid>
        ) : isLoading ? (
          <Loading />
        ) : (
          'Partner Shifts Report not available.'
        )}
      </MainCard>
    </Grid>
  );
};
